import type { MachineUpdateView, UpdateChannel } from "@racler/protocol";
import {
  availabilityLabel,
  blockerText,
  canUpdateNow,
  CHANNEL_OPTIONS,
  finishedJobText,
  hasUpdate,
  installedVersionText,
  isJobActive,
  jobStatusLabel,
  MANUAL_UPDATE_TITLE,
  manualUpdateSteps,
  updateTexts,
} from "../../lib/desktopUpdates.ts";

/** Installed Desktop version and architecture of one Machine ("…" while the update view is loading). */
export function MachineVersionCell({ view }: { view: MachineUpdateView | undefined }) {
  if (!view) return <span className="muted">{updateTexts.loading}</span>;
  const version = installedVersionText(view);
  return (
    <span className="machine-version">
      <span>{version.text}</span>
      {version.outdated && <span className="muted small"> {updateTexts.outdated}</span>}
      {view.client?.arch && <span className="badge machine-arch">{view.client.arch}</span>}
    </span>
  );
}

/**
 * Update state of one Machine: channel selector, newest allowed version, and the update action / progress / outcome.
 * Pure presentation: every request is made by the caller (and never for a blocked Machine).
 */
export function MachineUpdateCell({
  machineName,
  view,
  savingChannel,
  channelError,
  onChannelChange,
  onUpdateNow,
}: {
  machineName: string;
  view: MachineUpdateView | undefined;
  savingChannel: boolean;
  channelError: string | null;
  onChannelChange: (channel: UpdateChannel) => void;
  onUpdateNow: () => void;
}) {
  if (!view) return <span className="muted">{updateTexts.loading}</span>;
  const job = view.job;
  const active = isJobActive(job);
  const finished = job && !active ? finishedJobText(job) : null;
  const blocked = hasUpdate(view) && view.remoteBlocker !== null && !active;

  return (
    <div className="machine-update">
      <div className="machine-update-line">
        <select
          className="update-channel"
          aria-label={updateTexts.channelAria}
          value={view.channel}
          disabled={savingChannel || active}
          data-testid={`update-channel-${machineName}`}
          onChange={(e) => onChannelChange(e.target.value as UpdateChannel)}
        >
          {CHANNEL_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {savingChannel && <span className="muted small">{updateTexts.saving}</span>}
        {!savingChannel && view.online && !view.channelSynced && (
          <span className="muted small" data-testid={`update-syncing-${machineName}`}>
            {updateTexts.syncing}
          </span>
        )}
        <span
          className={`small ${view.availability === "required" ? "status warn" : view.availability === "optional" ? "" : "muted"}`}
          data-testid={`update-availability-${machineName}`}
        >
          {availabilityLabel(view)}
        </span>
        {canUpdateNow(view) && (
          <button className="primary" data-testid={`update-now-${machineName}`} onClick={onUpdateNow}>
            {updateTexts.updateNow}
          </button>
        )}
      </div>

      {channelError && (
        <p className="error small" data-testid={`update-channel-error-${machineName}`}>
          {channelError}
        </p>
      )}

      {active && (
        <div className="machine-update-line" data-testid={`update-progress-${machineName}`}>
          <span className="status warn">{jobStatusLabel(job.status)}</span>
          <span className="muted small">→ {job.targetVersion}</span>
          {job.status === "downloading" && job.progress !== null && (
            <>
              <div
                className="update-progress"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={job.progress}
              >
                <div className="update-progress-fill" style={{ width: `${job.progress}%` }} />
              </div>
              <span className="muted small">{job.progress}%</span>
            </>
          )}
        </div>
      )}

      {finished && (
        <p
          className={`status ${finished.tone} machine-update-result`}
          data-testid={`update-result-${machineName}`}
        >
          {finished.text}
        </p>
      )}

      {blocked && view.remoteBlocker && (
        <div className="machine-update-blocker" data-testid={`update-blocker-${machineName}`}>
          <p className="muted small">{blockerText(view.remoteBlocker)}</p>
          {view.remoteBlocker === "legacy_client" && (
            <details className="small">
              <summary>{MANUAL_UPDATE_TITLE}</summary>
              <ol>
                {manualUpdateSteps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
